import {
  RecurringChecklist,
  MBMarketingChecklistId,
  RecurringChecklistId,
  RecurringChecklistSectionId,
  RecurringChecklistSection,
  RecurringChecklistSectionStep,
  SetupChecklistSectionId,
  SetupChecklistSectionStepId,
  SetupChecklistSection,
  SetupChecklist,
  MBItemChecklists,
  AttachChecklistLineItemRequest,
} from './../../entities/marketing_v2/MarketingBoardResponse';
import { MarketingBoardAPI } from '@business/api/marketing_board_v2';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { switchMap, tap } from 'rxjs/operators';
import {
  MarketingBoardResponse,
  MarketingBoardId,
  RecurringChecklistSectionStepId,
  SetupChecklistSectionStep,
} from '@business/entities/marketing_v2';
import { PageQuery } from '@business/entities/common';
import { MarketingBoardFormData } from '@modules/MarketingV2/AddMarketingBoardModal/AddMarketingBoardModal';
import { ChecklistFormData } from '@modules/MarketingV2/AddChecklistModal/AddChecklistModal';
import { UserId, UserResponse } from '@business/entities/account';
import { showDeleteConfirmation } from '@modules/common';
import { of } from 'rxjs';
import { R } from '@core/utils/r';
import { Session } from '@modules/auth/session';
import { toMoment } from '@core/utils/time';

export class MarketingBoardBloc extends BaseBloc {
  private readonly marketingBoardRepo = new Repository<MarketingBoardResponse>({
    getItemId: item => item.id,
  });

  readonly boards$ = this.marketingBoardRepo.items$;
  readonly selectBoard = this.marketingBoardRepo.selectItem;

  onReset() {
    this.marketingBoardRepo.reset();
  }

  // fetchMarketingBoardItems = (query: PageQuery) => {
  //   return MarketingBoardAPI.fetchMarketingBoards(query).pipe(
  //     this.marketingBoardRepo.ops.reset(),
  //     handleMessage({ error: requestMessage('fetch_pros_error') }),
  //   );
  //};

  fetchMarketingBoardItem = (id: MarketingBoardId) => {
    return MarketingBoardAPI.fetchMarketingBoardItem(id).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_marketing_boards_error') }),
    );
  };

  fetchCompactMarketingBoardItem = (id: MarketingBoardId) => {
    return MarketingBoardAPI.fetchCompactMarketingBoardItem(id).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_marketing_boards_error') }),
    );
  };

  fetchMarketingBoardItems = (query: PageQuery) => {
    return MarketingBoardAPI.fetchMarketingBoards(query).pipe(
      tap(data => {
        this.marketingBoardRepo.reset(data.data);
      }),
      handleMessage({ error: requestMessage('fetch_marketing_boards_error') }),
    );
  };

  createMarketingBoardItem = (data: MarketingBoardFormData) => {
    return MarketingBoardAPI.createMarketingBoardItem(data).pipe(
      this.marketingBoardRepo.ops.addOne(item => item),
      handleMessage({ type: requestMessage('create_marketing_board') }),
    );
  };

  updateMarketingBoardItem = (id: MarketingBoardId, data: Omit<MarketingBoardFormData, 'orgId' | 'combineRecord' | 'checklist'>) => {
    return MarketingBoardAPI.updateMarketingBoardItem(id, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      //   this.marketingBoardRepo.ops.addOne(item => item),
      handleMessage({ type: requestMessage('create_marketing_board') }),
    );
  };

  deleteMarketingBoardItem = (id: MarketingBoardId) => {
    return showDeleteConfirmation('Delete Item', 'Do you really want to remove this record and all its checklists?').pipe(
      switchMap(() => MarketingBoardAPI.deleteMarketingBoardItem(id)),
      this.marketingBoardRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_marketing_board'),
      }),
    );
  };

  getMarketingBoardItemChecklist = (id: MBMarketingChecklistId) => {
    return MarketingBoardAPI.getMarketingBoardItemChecklist(id);
  };

  createMarketingBoardItemChecklist = (data: ChecklistFormData) => {
    return MarketingBoardAPI.createMarketingBoardItemChecklist(data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('create_marketing_board_checklist') }),
    );
  };

  updateMarketingBoardItemChecklist = (id: MBMarketingChecklistId, data: ChecklistFormData) => {
    return MarketingBoardAPI.updateMarketingBoardItemChecklist(id, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_marketing_board_checklist') }),
    );
  };
  attachLineItem = (id: MBMarketingChecklistId, data: AttachChecklistLineItemRequest) => {
    return MarketingBoardAPI.attachLineItem(id, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_marketing_board_checklist') }),
    );
  };

  deleteMarketingBoardItemChecklist = (id: MBMarketingChecklistId, boardId: MarketingBoardId) => {
    return showDeleteConfirmation('Delete Checklist', 'Do you really want to remove this checklist?').pipe(
      switchMap(() => MarketingBoardAPI.deleteMarketingBoardItemChecklist(id, boardId)),
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_checklist'),
      }),
    );
  };

  updateManager = (boardId: MarketingBoardId, manager: UserId) => {
    return MarketingBoardAPI.updateManager(boardId, manager).pipe(
      this.marketingBoardRepo.ops.updateOne(assignedTo => ({
        id: boardId,
        changes: () => ({
          assignedTo,
        }),
      })),
      handleMessage({
        type: requestMessage('save_marketing_board_settings'),
      }),
    );
  };

  updateExecutive = (boardId: MarketingBoardId, executive: UserId) => {
    return MarketingBoardAPI.updateExecutive(boardId, executive).pipe(
      this.marketingBoardRepo.ops.updateOne(user => ({
        id: boardId,
        changes: () => ({
          executive: user,
        }),
      })),
      handleMessage({
        type: requestMessage('save_marketing_board_settings'),
      }),
    );
  };

  updateRecurringChecklist = (id: RecurringChecklistId, boardId: MarketingBoardId, feedback: string, data: Partial<RecurringChecklist>) => {
    return MarketingBoardAPI.updateRecurringChecklist(id, boardId, feedback, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      //   handleMessage({
      //     type: requestMessage('update_recurring_checklist'),
      //   }),
    );
  };

  //   recurring checklist section
  createRecurringChecklistSection = (checklistId: MBMarketingChecklistId, boardId: MarketingBoardId, data: Partial<RecurringChecklistSection>) => {
    return MarketingBoardAPI.createRecurringChecklistSection(checklistId, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('error_on_section') }),
    );
  };

  updateRecurringChecklistSection = (id: RecurringChecklistSectionId, boardId: MarketingBoardId, data: Partial<RecurringChecklistSection>) => {
    return MarketingBoardAPI.updateRecurringChecklistSection(id, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_recurring_checklist_section'),
      }),
    );
  };

  deleteRecurringChecklistSection = (id: RecurringChecklistId, boardId: MarketingBoardId) => {
    return showDeleteConfirmation('Delete Recurring Section', 'Do you really want to remove this section?').pipe(
      switchMap(() => MarketingBoardAPI.deleteRecurringChecklistSection(id, boardId)),
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_recurring_checklist_section'),
      }),
    );
  };

  //   recurring checklist section step
  createRecurringChecklistSectionStep = (
    sectionId: RecurringChecklistSectionId,
    boardId: MarketingBoardId,
    data: Partial<RecurringChecklistSectionStep>,
  ) => {
    return MarketingBoardAPI.createRecurringChecklistSectionStep(sectionId, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('save_step') }),
    );
  };

  updateRecurringChecklistSectionStep = (
    id: RecurringChecklistSectionStepId,
    boardId: MarketingBoardId,
    data: Partial<RecurringChecklistSectionStep>,
  ) => {
    return MarketingBoardAPI.updateRecurringChecklistSectionStep(id, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('save_step'),
      }),
    );
  };

  deleteRecurringChecklistSectionStep = (id: RecurringChecklistId, boardId: MarketingBoardId) => {
    return showDeleteConfirmation('Delete Recurring Section', 'Do you really want to remove this section?').pipe(
      switchMap(() => MarketingBoardAPI.deleteRecurringChecklistSectionStep(id, boardId)),
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_recurring_checklist_section_step'),
      }),
    );
  };

  //   setup checklist section
  createSetupChecklistSection = (checklistId: MBMarketingChecklistId, boardId: MarketingBoardId, data: Partial<SetupChecklistSection>) => {
    return MarketingBoardAPI.createSetupChecklistSection(checklistId, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('error_on_section') }),
    );
  };

  updateSetupChecklistSection = (id: SetupChecklistSectionId, boardId: MarketingBoardId, data: Partial<SetupChecklistSection>) => {
    return MarketingBoardAPI.updateSetupChecklistSection(id, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_recurring_checklist_section'),
      }),
    );
  };

  deleteSetupChecklistSection = (id: SetupChecklistSectionId, boardId: MarketingBoardId) => {
    return showDeleteConfirmation('Delete Recurring Section', 'Do you really want to remove this section?').pipe(
      switchMap(() => MarketingBoardAPI.deleteSetupChecklistSection(id, boardId)),
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_recurring_checklist_section'),
      }),
    );
  };

  //   setup checklist section step
  createSetupChecklistSectionStep = (sectionId: SetupChecklistSectionId, boardId: MarketingBoardId, data: Partial<SetupChecklistSectionStep>) => {
    return MarketingBoardAPI.createSetupChecklistSectionStep(sectionId, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('save_step') }),
    );
  };

  updateSetupChecklistSectionStep = (id: SetupChecklistSectionStepId, boardId: MarketingBoardId, data: Partial<SetupChecklistSectionStep>) => {
    return MarketingBoardAPI.updateSetupChecklistSectionStep(id, boardId, data).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('save_step'),
      }),
    );
  };

  deleteSetupChecklistSectionStep = (id: RecurringChecklistId, boardId: MarketingBoardId) => {
    return showDeleteConfirmation('Delete Section Step', 'Do you really want to remove this step?').pipe(
      switchMap(() => MarketingBoardAPI.deleteSetupChecklistSectionStep(id, boardId)),
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_setup_checklist_section_step'),
      }),
    );
  };

  toggleStepNew = (boardId: MarketingBoardId, type: 'setup' | 'recurring', stepId: string, all?: boolean) => {
    return MarketingBoardAPI.toggleStepNew(boardId, type, stepId, all);
    // return MarketingBoardAPI.toggleStepNew(boardId, type, stepId, all).pipe(this.marketingBoardRepo.ops.upsertOne(item => ({ item })));
  };

  toggleStep = (
    boardId: MarketingBoardId,
    checklistId: MBMarketingChecklistId,
    recurringChecklistId: RecurringChecklistId,
    sectionId: SetupChecklistSectionId,
    stepId: SetupChecklistSectionStepId,
    checked: boolean,
    type: 'setup' | 'recurring',
    all?: boolean,
  ) => {
    return of(null).pipe(
      tap(() => {
        const marketBoard = this.marketingBoardRepo.getItem(boardId)! as MarketingBoardResponse;
        const checklist = this.getChecklist(marketBoard, checklistId)!;
        let section: SetupChecklistSection;
        if (recurringChecklistId == '0') {
          section = this.getSection(checklist.setupChecklists as SetupChecklist, sectionId)! as SetupChecklistSection;
        } else {
          const recurringChecklist = this.getRecurringChecklist(recurringChecklistId, checklist);
          section = this.getSection(recurringChecklist as RecurringChecklist, sectionId)! as RecurringChecklistSection;
        }
        const step = this.getStep(section, stepId)! as SetupChecklistSectionStep;
        step.isCompleted = !step.isCompleted;

        if (step.isCompleted) {
          step.completedAt = toMoment().unix();
          step.completedBy = Session.user! as UserResponse;
        } else {
          step.completedAt = null! as number;
          step.completedBy = null! as UserResponse;
        }
        this.updateStep(marketBoard, checklistId, recurringChecklistId, sectionId, step);
      }),
      switchMap(() => {
        return MarketingBoardAPI.toggleStepNew(boardId, type, stepId, all);
        // if (recurringChecklistId == '0')
        //   return MarketingBoardAPI.setupToggleStep(boardId, checklistId, sectionId, stepId, recurringChecklistId, checked);
        // else return MarketingBoardAPI.recurringToggleStep(boardId, checklistId, sectionId, stepId, recurringChecklistId, checked);
      }),
      //   this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      //   handleMessage({
      //     error: requestMessage('step_updated'),
      //   }),
    );
  };

  updateStep = (
    inBoard: MarketingBoardResponse,
    checklistId: MBMarketingChecklistId,
    recurringChecklistId: RecurringChecklistId,
    sectionId: string,
    updatedStep: SetupChecklistSectionStep,
  ) => {
    const board = R.cloneDeep(inBoard);
    const checklists = R.find(board.itemChecklists, s => s.id === checklistId)!;
    let section: SetupChecklistSection | RecurringChecklistSection;
    if (recurringChecklistId == '0') {
      section = R.find(checklists.setupChecklists.sections, s => s.id === sectionId)!;
    } else {
      const recurringChecklist = R.find(checklists.recurringChecklists, s => s.id === recurringChecklistId)! as RecurringChecklist;
      section = R.find(recurringChecklist.sections, s => s.id === sectionId)!;
    }
    const step = R.find(section.steps, s => s.id === updatedStep.id)! as SetupChecklistSectionStep | RecurringChecklistSectionStep;

    step.isCompleted = updatedStep.isCompleted;
    step.completedAt = updatedStep.completedAt;
    step.completedBy = updatedStep.completedBy;
    this.marketingBoardRepo.updateOne({
      id: board.id,
      changes: () => board,
    });
  };

  getStep = (sections: SetupChecklistSection, stepId: SetupChecklistSectionStepId) => {
    const cloneSections = R.cloneDeep(sections);
    return R.find(cloneSections.steps, s => s.id === stepId);
  };

  getRecurringChecklist = (recurringChecklistId: RecurringChecklistId, checkList: MBItemChecklists) => {
    const mainChecklist = R.cloneDeep(checkList);
    return R.find(mainChecklist.recurringChecklists, s => s.id === recurringChecklistId);
  };

  getSection = (checkList: SetupChecklist | RecurringChecklist, sectionId: SetupChecklistSectionId | RecurringChecklistSectionId) => {
    const recurringSections = R.cloneDeep(checkList);
    return R.find(recurringSections.sections, s => s.id === sectionId);
  };

  getChecklist = (marketBoard: MarketingBoardResponse, checklistId: MBMarketingChecklistId) => {
    const board = R.cloneDeep(marketBoard);
    return R.find(board.itemChecklists, s => s.id === checklistId);
  };

  sortRecurringChecklistSectionStep = (stepIds: string[], boardId: MarketingBoardId) => {
    return MarketingBoardAPI.sortRecurringChecklistSectionSteps(stepIds, boardId).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      //   handleMessage({
      //     type: requestMessage('sort_setup_checklist_section_step'),
      //   }),
    );
  };

  sortSetupChecklistSection = (sectionIds: string[], boardId: MarketingBoardId) => {
    return MarketingBoardAPI.sortSetupChecklistSections(sectionIds, boardId).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      //   handleMessage({
      //     type: requestMessage('sort_setup_checklist_section'),
      //   }),
    );
  };

  sortRecurringChecklistSection = (sectionIds: string[], boardId: MarketingBoardId) => {
    return MarketingBoardAPI.sortRecurringChecklistSections(sectionIds, boardId).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      //   handleMessage({
      //     type: requestMessage('sort_setup_checklist_section'),
      //   }),
    );
  };

  order = (type: 'setup' | 'recurring', boardId: string, checklistId: string, items: any[]) => {
    return MarketingBoardAPI.order(type, boardId, checklistId, items).pipe(this.marketingBoardRepo.ops.upsertOne(item => ({ item })));
  };

  updateUsers = (boardId: string, type: 'setup' | 'recurring', stepId: string, users: string[]) => {
    return MarketingBoardAPI.updateUsers(boardId, type, stepId, users).pipe(this.marketingBoardRepo.ops.upsertOne(item => ({ item })));
  };

  sortSteps = (boardId: string, type: 'setup' | 'recurring', stepIds: string[]) => {
    return MarketingBoardAPI.sortSteps(boardId, type, stepIds).pipe(this.marketingBoardRepo.ops.upsertOne(item => ({ item })));
  };
}
